import { useEffect, useRef, useState } from "react";
import { apiBase } from "../api";
import { useI18n } from "../i18n";
import { APP_VERSION, isNativeRuntime, nativePlatform, WEB_BUNDLE } from "./platform";

/**
 * (Task Q) Over-the-air updates of the web layer with the open-source Capgo updater (@capgo/capacitor-updater,
 * MPL-2.0) in self-hosted manual mode: autoUpdate off, no Capgo cloud (stats/channel URLs empty in capacitor.config).
 *
 *  - on launch and on resume (at most every 30 min) ask our API what to do (GET /api/app/update)
 *  - "update": download in the background (the plugin verifies the sha256 checksum) and apply on the next app start
 *    (next) — or right away behind a "正在更新…" screen when the release is critical or a rollback (set)
 *  - "reset": the channel went back to the app's built-in web → reset
 *  - "native_required": the release needs a newer APK / IPA → "请下载新版 App" with the store / APK link
 *  - notifyAppReady() on every start: a bundle that can't start is rolled back by the plugin automatically
 *
 * (OTA fix) A Capacitor plugin object is a Proxy that answers EVERY property with a native-method wrapper — including
 * "then". So it is thenable: resolving a Promise with it (import(...).then((m) => m.CapacitorUpdater), or returning
 * the plugin from an async function) makes the engine call Plugin.then(resolve, reject), which only rejects
 * internally ("CapacitorUpdater.then() is not implemented on android") and NEVER settles the outer promise. Up to
 * 0.4.0 that hung plugin() forever: no notifyAppReady, no GET /api/app/update, no OTA at all. The plugin is therefore
 * only ever handed around inside a box ({ U }), and every native call has a timeout so nothing can block the check.
 */
export type OtaManifest =
  | { action: "none"; reason?: string }
  | { action: "update"; version: string; url: string; checksum: string; sizeBytes: number; critical: boolean; rollback: boolean; notes: string | null; minNativeVersion: string }
  | { action: "reset"; rollback: true; critical: boolean }
  | { action: "native_required"; minNativeVersion: string; downloadUrl: string | null; version: string };

export const OTA_MIN_INTERVAL_MS = 30 * 60_000;
/** Native calls that should answer at once (current, list, next, notifyAppReady). */
export const OTA_CALL_TIMEOUT_MS = 8_000;
/** The update manifest request. */
export const OTA_FETCH_TIMEOUT_MS = 15_000;
/** Bundle download (a few hundred KB, but slow shop Wi-Fi). */
export const OTA_DOWNLOAD_TIMEOUT_MS = 180_000;
const LAST_KEY = "spos.ota.lastCheck";
const CHANNEL_KEY = "spos.ota.channel";

/** At most one check per 30 minutes (launch always checks). */
export function shouldCheck(lastCheckAt: number | null, now: number, launch = false): boolean {
  return launch || lastCheckAt === null || now - lastCheckAt >= OTA_MIN_INTERVAL_MS;
}
/** What the app does with a manifest. */
export type OtaPlan = "none" | "download_next" | "download_now" | "reset_now" | "native_required";
export function planOta(m: OtaManifest): OtaPlan {
  switch (m.action) {
    case "update": return m.critical || m.rollback ? "download_now" : "download_next";
    case "reset": return "reset_now";
    case "native_required": return "native_required";
    default: return "none";
  }
}
export const otaChannel = () => { try { return localStorage.getItem(CHANNEL_KEY) === "beta" ? "beta" : "production"; } catch { return "production"; } };
export function updateUrl(base: string, q: { platform: string; appVersion: string; channel: string; bundle: string }) {
  return base + "/api/app/update?" + new URLSearchParams(q).toString();
}

/** Rejects after ms (a native call that never answers must not block the update check). */
export function withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(what + " timed out")), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

type Updater = typeof import("@capgo/capacitor-updater").CapacitorUpdater;
/** Boxed on purpose — never resolve a promise with a Capacitor plugin proxy (it is thenable, see above). */
type Box = { U: Updater };
let updater: Promise<Box | null> | null = null;
const capAvailable = (name: string) => {
  try {
    const c = (window as unknown as { Capacitor?: { isPluginAvailable?: (n: string) => boolean } }).Capacitor;
    return typeof c?.isPluginAvailable === "function" ? c.isPluginAvailable(name) : true;
  } catch { return true; }
};
const plugin = (): Promise<Box | null> => (updater ??= !capAvailable("CapacitorUpdater")
  ? Promise.resolve(null)
  : import("@capgo/capacitor-updater").then((m): Box => ({ U: m.CapacitorUpdater })).catch(() => null));
/** Tests only. */
export function resetOtaForTests() { updater = null; lastResult = null; gateStarted = false; }

export interface OtaStatus { native: string; bundle: string; plugin: boolean }
/** What runs now: native app version + web bundle ("builtin" = the web inside the APK). Never hangs. */
export async function otaStatus(): Promise<OtaStatus | null> {
  if (!isNativeRuntime()) return null;
  const box = await plugin();
  const fallback = { native: APP_VERSION.split(" ")[0], bundle: WEB_BUNDLE || "builtin" };
  if (!box) return { ...fallback, plugin: false };
  try {
    const c = await withTimeout(box.U.current(), OTA_CALL_TIMEOUT_MS, "current()");
    return { native: c.native || fallback.native, bundle: c.bundle.id === "builtin" || !c.bundle.version ? "builtin" : c.bundle.version, plugin: true };
  } catch { return { ...fallback, plugin: true }; }
}

export interface OtaResult {
  plan: OtaPlan;
  manifest: OtaManifest | null;
  /** "skipped" = within the 30 min window; "error" = see error (never thrown, never silent). */
  outcome: "skipped" | "checked" | "error";
  error?: string;
  /** id of the downloaded bundle (update → applied on the next start, or now with applyDownloaded) */
  bundleId?: string;
  at: number;
}
let lastResult: OtaResult | null = null;
/** The last check of this app session (Settings shows it). */
export const lastOtaResult = () => lastResult;

/** One check: returns what happened so the UI can show the right screen. Never hangs; errors come back as outcome "error". */
export async function checkForUpdate(opts: { launch?: boolean; force?: boolean; onApplying?: () => void } = {}): Promise<OtaResult> {
  const now = Date.now();
  if (!isNativeRuntime()) return { plan: "none", manifest: null, outcome: "skipped", at: now };
  let last: number | null = null;
  try { const v = localStorage.getItem(LAST_KEY); last = v ? Number(v) : null; } catch { /* */ }
  if (!opts.force && !shouldCheck(last, now, opts.launch)) return { plan: "none", manifest: null, outcome: "skipped", at: now };
  const done = (r: Omit<OtaResult, "at">): OtaResult => (lastResult = { ...r, at: now });
  let applying = false;
  try {
    const box = await plugin();
    let native = APP_VERSION.split(" ")[0];
    let bundle = WEB_BUNDLE || "builtin";
    if (box) {
      const cur = await withTimeout(box.U.current(), OTA_CALL_TIMEOUT_MS, "current()").catch(() => null);
      if (cur) {
        native = cur.native || native;
        bundle = cur.bundle.id === "builtin" || !cur.bundle.version ? "builtin" : cur.bundle.version;
      }
    }
    const ctl = typeof AbortController === "function" ? new AbortController() : null;
    const timer = setTimeout(() => ctl?.abort(), OTA_FETCH_TIMEOUT_MS);
    let res: Response;
    try {
      res = await fetch(updateUrl(apiBase(), { platform: nativePlatform() === "ios" ? "ios" : "android", appVersion: native, channel: otaChannel(), bundle }), { credentials: "omit", cache: "no-store", signal: ctl?.signal });
    } finally { clearTimeout(timer); }
    if (!res.ok) return done({ plan: "none", manifest: null, outcome: "error", error: "HTTP " + res.status });
    const m = (await res.json()) as OtaManifest;
    try { localStorage.setItem(LAST_KEY, String(now)); } catch { /* */ }
    const plan = planOta(m);
    if (!box && (m.action === "update" || m.action === "reset")) return done({ plan, manifest: m, outcome: "error", error: "updater plugin missing" });
    if (m.action === "update" && box) {
      const { U } = box;
      const list = await withTimeout(U.list(), OTA_CALL_TIMEOUT_MS, "list()").catch(() => ({ bundles: [] as Array<{ id: string; version: string; status: string }> }));
      const known = list.bundles.filter((x) => x.version === m.version);
      // a bundle that already failed to start on this phone (the plugin rolled it back) is not downloaded again and again
      if (known.length && known.every((x) => x.status === "error")) return done({ plan: "none", manifest: m, outcome: "error", error: "bundle " + m.version + " failed to start before" });
      let b = known.find((x) => x.status !== "error");
      // the plugin checks the sha256 of the zip against the manifest and refuses a mismatch
      if (!b) b = await withTimeout(U.download({ url: m.url, version: m.version, checksum: m.checksum }), OTA_DOWNLOAD_TIMEOUT_MS, "download()");
      if (plan === "download_now") { applying = true; opts.onApplying?.(); await U.set({ id: b.id }); }
      else await withTimeout(U.next({ id: b.id }), OTA_CALL_TIMEOUT_MS, "next()");
      return done({ plan, manifest: m, outcome: "checked", bundleId: b.id });
    }
    if (m.action === "reset" && box) {
      applying = true;
      opts.onApplying?.();
      await box.U.reset({ toLastSuccessful: false });
    }
    return done({ plan, manifest: m, outcome: "checked" });
  } catch (e) {
    const r = done({ plan: "none", manifest: null, outcome: "error", error: String((e as Error)?.message ?? e).slice(0, 200) });
    // the "Updating…" screen is up: the caller must take it down
    if (applying) throw Object.assign(new Error(r.error), { otaResult: r });
    return r;
  }
}

/** Apply a downloaded bundle right away (reloads the web view) — "Restart now" after a manual check. */
export async function applyDownloaded(id: string) {
  const box = await plugin();
  if (!box) throw new Error("updater plugin missing");
  await box.U.set({ id });
}

/** Tell the plugin this bundle started fine (otherwise it rolls back after appReadyTimeout). Never hangs. */
export async function notifyReady(): Promise<boolean> {
  const box = await plugin();
  if (!box) return false;
  try { await withTimeout(box.U.notifyAppReady(), OTA_CALL_TIMEOUT_MS, "notifyAppReady()"); return true; } catch { return false; }
}

/** Mounted once (native only): notifyAppReady, check on launch + resume, the update / new-app screens. */
export function OtaGate() {
  if (!isNativeRuntime()) return null;
  return <Gate />;
}
let gateStarted = false;
function Gate() {
  const { t } = useI18n();
  const [applying, setApplying] = useState(false);
  const [native, setNative] = useState<Extract<OtaManifest, { action: "native_required" }> | null>(null);
  const busy = useRef(false);
  useEffect(() => {
    const run = async (launch: boolean) => {
      if (busy.current) return;
      busy.current = true;
      try {
        const r = await checkForUpdate({ launch, onApplying: () => setApplying(true) });
        if (r.manifest?.action === "native_required") setNative(r.manifest);
      } catch { setApplying(false); } finally { busy.current = false; }
    };
    // the launch check (and notifyAppReady) run once per app start, even if the gate remounts
    if (!gateStarted) { gateStarted = true; void notifyReady().finally(() => void run(true)); }
    let alive = true;
    let remove: (() => void) | undefined;
    void import("@capacitor/app").then(async ({ App }) => {
      const h = await App.addListener("appStateChange", ({ isActive }) => { if (isActive) void run(false); });
      if (alive) remove = () => void h.remove(); else void h.remove();
    }).catch(() => undefined);
    return () => { alive = false; remove?.(); };
  }, []);
  if (applying) return (
    <div className="ota-overlay" role="alert" aria-busy="true" data-testid="ota-updating">
      <div><div className="spinner" aria-hidden /><h2>{t("tq.ota.updating")}</h2><p className="muted">{t("tq.ota.updatingSub")}</p></div>
    </div>
  );
  if (native) return (
    <div className="overlay dialog-overlay" role="dialog" aria-modal="true" aria-label={t("tq.ota.native")} data-testid="ota-native">
      <div className="dialog">
        <h2>{t("tq.ota.native")}</h2>
        <p>{t("tq.ota.nativeSub", { v: native.minNativeVersion })}</p>
        <div className="row gap">
          {native.downloadUrl && <a className="btn btn-primary" href={native.downloadUrl} target="_blank" rel="noreferrer">{t("tq.ota.download")}</a>}
          <button className="btn btn-quiet" onClick={() => setNative(null)}>{t("tq.ota.later")}</button>
        </div>
      </div>
    </div>
  );
  return null;
}

/** (OTA fix) Settings → "This device": which app + web bundle runs, the last check, "Check for updates" (forced). */
export function OtaVersionRow() {
  const { t } = useI18n();
  const [st, setSt] = useState<OtaStatus | null>(null);
  const [res, setRes] = useState<OtaResult | null>(lastOtaResult());
  const [busy, setBusy] = useState(false);
  const [restarting, setRestarting] = useState(false);
  useEffect(() => { void otaStatus().then(setSt); }, []);
  const check = async () => {
    setBusy(true);
    try { setRes(await checkForUpdate({ force: true })); } catch (e) { setRes((e as { otaResult?: OtaResult }).otaResult ?? null); }
    finally { setBusy(false); void otaStatus().then(setSt); }
  };
  const restart = async (id: string) => { setRestarting(true); try { await applyDownloaded(id); } catch { setRestarting(false); } };
  const msg = !res || res.outcome === "skipped" ? null
    : res.outcome === "error" ? t("ota.fix.error", { e: res.error ?? "?" })
    : res.plan === "none" ? t("tq.ota.upToDate")
    : res.plan === "download_next" ? t("tq.ota.ready")
    : res.manifest?.action === "native_required" ? t("tq.ota.nativeSub", { v: res.manifest.minNativeVersion })
    : t("tq.ota.updating");
  return (
    <div className="stack" data-testid="ota-version-row">
      <div className="row-between">
        <div>
          <strong>{t("ota.fix.title")}</strong>
          <p className="muted small" data-testid="ota-version">{t("tq.ota.version", { native: st?.native ?? APP_VERSION, bundle: st?.bundle ?? (WEB_BUNDLE || "builtin") })}</p>
          {st && !st.plugin && <p className="muted small">{t("ota.fix.noPlugin")}</p>}
        </div>
        <button className="btn btn-soft" data-testid="ota-check" disabled={busy || restarting} onClick={() => void check()}>{busy ? t("ota.fix.checking") : t("tq.ota.check")}</button>
      </div>
      {msg && <p className={"small" + (res?.outcome === "error" ? " warn-text" : "")} role="status" data-testid="ota-check-result">{msg}{" · " + new Date(res!.at).toLocaleTimeString()}</p>}
      {res?.plan === "download_next" && res.bundleId && <button className="btn btn-primary" data-testid="ota-restart" disabled={restarting} onClick={() => void restart(res.bundleId!)}>{t("ota.fix.restart")}</button>}
      {res?.manifest?.action === "native_required" && res.manifest.downloadUrl && <a className="btn btn-primary" href={res.manifest.downloadUrl} target="_blank" rel="noreferrer">{t("tq.ota.download")}</a>}
    </div>
  );
}
